import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  LinearProgress,
  Autocomplete,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Search,
  EmojiEvents,
  Person,
  Star,
  Title
} from '@mui/icons-material';
import { adminAchievementService } from '../../../services/adminAchievementService';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

const UserAchievementManager = () => {
  const [userAchievements, setUserAchievements] = useState([]);
  const [userBadges, setUserBadges] = useState([]);
  const [users, setUsers] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [badges, setBadges] = useState([]);
  const [titles, setTitles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    userID: '',
    achievementType: '',
    badgeType: '',
    titleType: '',
    unlocked: false,
    progress: 0,
    metadata: {}
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadUserAchievements(selectedUser.id);
    }
  }, [selectedUser]);

  const loadData = async () => {
    try {
      const [usersData, achievementsData, badgesData, titlesData] = await Promise.all([
        adminAchievementService.getAllUsers(),
        adminAchievementService.getAllAchievements(),
        adminAchievementService.getAllBadges(),
        adminAchievementService.getAllTitles()
      ]);
      setUsers(usersData);
      setAchievements(achievementsData);
      setBadges(badgesData);
      setTitles(titlesData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadUserAchievements = async (userId) => {
    try {
      const [achievementsData, badgesData] = await Promise.all([
        adminAchievementService.getUserAchievements(userId),
        adminAchievementService.getUserBadges(userId)
      ]);
      setUserAchievements(achievementsData);
      setUserBadges(badgesData);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleGrantItem = async () => {
    try {
      console.log('=== GRANT ITEM FLOW START ===');
      console.log('Active tab:', activeTab);
      console.log('Selected user:', selectedUser);
      console.log('Form data:', formData);
      
      // Check current user auth
      const { getCurrentUser } = await import('@aws-amplify/auth');
      try {
        const currentUser = await getCurrentUser();
        console.log('Current authenticated user:', currentUser);
        console.log('User groups:', currentUser.signInUserSession?.accessToken?.payload?.['cognito:groups']);
      } catch (authError) {
        console.error('Auth check error:', authError);
      }
      
      if (activeTab === 0) {
        // Grant Achievement
        console.log('Granting achievement...');
        await adminAchievementService.grantAchievement(selectedUser.id, formData.achievementType);
      } else if (activeTab === 1) {
        // Grant Badge
        console.log('Granting badge...');
        await adminAchievementService.grantBadge(selectedUser.id, formData.badgeType);
      } else if (activeTab === 2) {
        // Update User Title
        console.log('=== TITLE ASSIGNMENT FLOW START ===');
        console.log('Selected User:', selectedUser);
        console.log('Form Data:', formData);
        console.log('Title Type to assign:', formData.titleType);
        
        try {
          const result = await adminAchievementService.updateUserTitle(selectedUser.id, formData.titleType);
          console.log('Title assignment result:', result);
          console.log('=== TITLE ASSIGNMENT SUCCESS ===');
          
          // Direct query to verify database update
          const GET_USER_DIRECT = `
            query GetUser($id: ID!) {
              getUser(id: $id) {
                id
                username
                displayName
                titles
                titleOrder
                updatedAt
              }
            }
          `;
          
          const directUserQuery = await client.graphql({
            query: GET_USER_DIRECT,
            variables: { id: selectedUser.id }
          });
          
          console.log('Direct database query result:', directUserQuery.data.getUser);
          
          if (directUserQuery.data.getUser) {
            setSelectedUser(prev => ({
              ...prev,
              titles: directUserQuery.data.getUser.titles,
              titleOrder: directUserQuery.data.getUser.titleOrder,
              updatedAt: directUserQuery.data.getUser.updatedAt
            }));
          }
        } catch (titleError) {
          console.error('=== TITLE ASSIGNMENT ERROR ===');
          console.error('Error details:', titleError);
          console.error('Error data:', titleError.data);
          console.error('Error errors:', titleError.errors);
          throw titleError;
        }
      }
      await loadUserAchievements(selectedUser.id);
      handleCloseDialog();
      console.log('=== GRANT ITEM FLOW SUCCESS ===');
    } catch (error) {
      console.error('=== GRANT ITEM FLOW ERROR ===');
      console.error('Error granting item:', error);
      console.error('Error data:', error.data);
      console.error('Error errors:', error.errors);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`Error ${index + 1}:`, err.message);
          console.error('Error details:', err);
        });
      }
    }
  };

  const handleRevokeAchievement = async (achievementId) => {
    if (window.confirm('Are you sure you want to revoke this achievement?')) {
      try {
        await adminAchievementService.revokeAchievement(achievementId);
        await loadUserAchievements(selectedUser.id);
      } catch (error) {
        console.error('Error revoking achievement:', error);
      }
    }
  };

  const handleRevokeBadge = async (badgeId) => {
    if (window.confirm('Are you sure you want to revoke this badge?')) {
      try {
        await adminAchievementService.revokeBadge(badgeId);
        await loadUserAchievements(selectedUser.id);
      } catch (error) {
        console.error('Error revoking badge:', error);
      }
    }
  };

  const handleUpdateProgress = async (achievementId, newProgress) => {
    try {
      await adminAchievementService.updateAchievementProgress(achievementId, newProgress);
      await loadUserAchievements(selectedUser.id);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleRemoveTitle = async () => {
    if (window.confirm('Are you sure you want to remove this user\'s title?')) {
      try {
        await adminAchievementService.removeUserTitle(selectedUser.id);
        
        const GET_USER_DIRECT = `
          query GetUser($id: ID!) {
            getUser(id: $id) {
              id
              username
              displayName
              titles
              titleOrder
              updatedAt
            }
          }
        `;
        
        const directUserQuery = await client.graphql({
          query: GET_USER_DIRECT,
          variables: { id: selectedUser.id }
        });
        
        if (directUserQuery.data.getUser) {
          setSelectedUser(prev => ({
            ...prev,
            titles: directUserQuery.data.getUser.titles,
            titleOrder: directUserQuery.data.getUser.titleOrder,
            updatedAt: directUserQuery.data.getUser.updatedAt
          }));
        }
      } catch (error) {
        console.error('Error removing title:', error);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      userID: '',
      achievementType: '',
      badgeType: '',
      titleType: '',
      unlocked: false,
      progress: 0,
      metadata: {}
    });
  };

  const getAchievementDefinition = (type) => {
    return achievements.find(a => a.type === type) || {};
  };

  const filteredUsers = users.filter(user =>
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Achievement Management
      </Typography>

      <Grid container spacing={3}>
        {/* User Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select User
              </Typography>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredUsers.map((user) => (
                  <Card
                    key={user.id}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      border: selectedUser?.id === user.id ? 2 : 1,
                      borderColor: selectedUser?.id === user.id ? 'primary.main' : 'divider'
                    }}
                    onClick={() => setSelectedUser(user)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {user.displayName?.[0] || user.username?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {user.displayName || user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* User Achievements */}
        <Grid item xs={12} md={8}>
          {selectedUser ? (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    User Management for {selectedUser.displayName || selectedUser.username}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setDialogOpen(true)}
                  >
                    {activeTab === 0 ? 'Grant Achievement' : activeTab === 1 ? 'Grant Badge' : 'Assign Title'}
                  </Button>
                </Box>

                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
                  <Tab icon={<EmojiEvents />} label="Achievements" />
                  <Tab icon={<Star />} label="Badges" />
                  <Tab icon={<Title />} label="Titles" />
                </Tabs>

                {activeTab === 0 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Achievement</TableCell>
                          <TableCell>Progress</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Unlocked</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userAchievements.map((userAchievement) => {
                          const achievement = getAchievementDefinition(userAchievement.achievementType);
                          const progressPercent = (userAchievement.progress / (achievement.targetValue || 1)) * 100;

                          return (
                            <TableRow key={userAchievement.id}>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <EmojiEvents />
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {achievement.name || userAchievement.achievementType}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {achievement.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ minWidth: 100 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(progressPercent, 100)}
                                    sx={{ mb: 0.5 }}
                                  />
                                  <Typography variant="caption">
                                    {userAchievement.progress} / {achievement.targetValue || 1}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={userAchievement.unlocked ? 'Unlocked' : 'In Progress'}
                                  color={userAchievement.unlocked ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {userAchievement.unlockedAt ? 
                                  new Date(userAchievement.unlockedAt).toLocaleDateString() : 
                                  '-'
                                }
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevokeAchievement(userAchievement.id)}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {activeTab === 1 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Badge</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Earned Date</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userBadges.map((userBadge) => {
                          const badge = badges.find(b => b.type === userBadge.badgeType) || {};
                          return (
                            <TableRow key={userBadge.id}>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Star sx={{ color: badge.color || '#FFD700' }} />
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {badge.name || userBadge.badgeType}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {badge.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={userBadge.badgeType} size="small" />
                              </TableCell>
                              <TableCell>
                                {new Date(userBadge.earnedAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevokeBadge(userBadge.id)}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {activeTab === 2 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      User Titles Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      User can have one active title at a time. Assigning a new title will replace the current one.
                    </Typography>
                    
                    {selectedUser.titles && selectedUser.titles.length > 0 ? (
                      <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Title</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>User</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedUser.titles.map((titleType, index) => {
                              const titleDef = titles.find(t => t.type === titleType) || {};
                              return (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Title sx={{ color: titleDef.color || '#1976d2' }} />
                                      <Box>
                                        <Typography variant="subtitle2">
                                          {titleDef.name || titleType}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {titleDef.description || 'Custom title'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={titleType} 
                                      size="small" 
                                      sx={{ bgcolor: titleDef.color || '#1976d2', color: 'white' }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {selectedUser.displayName || selectedUser.username}
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveTitle()}
                                      title="Remove title"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ mt: 2, p: 3, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                        <Title sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No title currently assigned to this user.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Select a user to manage their achievements
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Grant Item Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {activeTab === 0 ? 'Grant Achievement' : activeTab === 1 ? 'Grant Badge' : 'Assign Title'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {activeTab === 0 && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Achievement</InputLabel>
                    <Select
                      value={formData.achievementType}
                      onChange={(e) => setFormData(prev => ({ ...prev, achievementType: e.target.value }))}
                    >
                      {achievements.map((achievement) => (
                        <MenuItem key={achievement.type} value={achievement.type}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmojiEvents />
                            <Box>
                              <Typography variant="subtitle2">{achievement.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {achievement.description}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Progress"
                    type="number"
                    value={formData.progress}
                    onChange={(e) => setFormData(prev => ({ ...prev, progress: parseInt(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.unlocked}
                      onChange={(e) => setFormData(prev => ({ ...prev, unlocked: e.target.value }))}
                    >
                      <MenuItem value={false}>In Progress</MenuItem>
                      <MenuItem value={true}>Unlocked</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            {activeTab === 1 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Badge</InputLabel>
                  <Select
                    value={formData.badgeType}
                    onChange={(e) => setFormData(prev => ({ ...prev, badgeType: e.target.value }))}
                  >
                    {badges.map((badge) => (
                      <MenuItem key={badge.type} value={badge.type}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Star sx={{ color: badge.color }} />
                          <Box>
                            <Typography variant="subtitle2">{badge.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {badge.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {activeTab === 2 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Title</InputLabel>
                  <Select
                    value={formData.titleType}
                    onChange={(e) => setFormData(prev => ({ ...prev, titleType: e.target.value }))}
                  >
                    {titles.map((title) => (
                      <MenuItem key={title.type} value={title.type}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Title sx={{ color: title.color }} />
                          <Box>
                            <Typography variant="subtitle2">{title.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {title.description}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button onClick={handleGrantItem} variant="contained" startIcon={<Save />}>
            {activeTab === 0 ? 'Grant Achievement' : activeTab === 1 ? 'Grant Badge' : 'Assign Title'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAchievementManager;